import React, { useEffect, useState } from "react";
import { Modal, Space, Spin, Statistic, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { CampaignItemTotal, GetCampaignPaidOrdersResponse } from "../../types/api";
import { Campaign } from "../../types/model";
import { getCampaignPaidOrders } from "./api";

const { Text } = Typography;

interface CampaignSummaryModalProps {
  restaurantId: string;
  // The modal is open while a campaign is set.
  campaign: Campaign | null;
  onClose: () => void;
}

// Shows the number of paid orders for a campaign and how many of each item
// (by name and modifications) were ordered across those orders.
export const CampaignSummaryModal: React.FC<CampaignSummaryModalProps> = ({
  restaurantId,
  campaign,
  onClose,
}) => {
  const { t } = useTranslation();
  const { notifyError } = useNotification();
  const [summary, setSummary] = useState<GetCampaignPaidOrdersResponse | null>(null);

  useEffect(() => {
    setSummary(null);
    if (!campaign) {
      return;
    }

    let cancelled = false;
    getCampaignPaidOrders(
      restaurantId,
      campaign.id,
      (data) => {
        if (!cancelled) {
          setSummary(data);
        }
      },
      (err) => {
        if (!cancelled) {
          notifyError(t("campaign_summary_load_failed"), err);
          onClose();
        }
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, campaign]);

  const columns = [
    {
      title: t("campaign_item_name"),
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: t("campaign_item_modifications"),
      dataIndex: "item_modifications",
      key: "item_modifications",
      render: (mods: string) => mods || <Text type="secondary">—</Text>,
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
    },
  ];

  return (
    <Modal
      title={campaign?.name}
      open={campaign !== null}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnHidden
    >
      {summary === null ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Statistic title={t("campaign_paid_orders")} value={summary.paid_order_count} />
          <Table<CampaignItemTotal>
            rowKey={(row) => `${row.item_name}\u0000${row.item_modifications}`}
            dataSource={summary.item_totals}
            columns={columns}
            pagination={false}
            size="small"
            summary={(rows) => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong>{t("total_quantity")}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong>{rows.reduce((sum, row) => sum + row.quantity, 0)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Space>
      )}
    </Modal>
  );
};
